import { useState, useMemo, useEffect, useRef } from "react";
import {
  formatAddressShort,
  getSpaceDisplayName,
  allSpacesMatchLocation,
} from "../utils";
import { X } from "lucide-react";
import { useProposalT } from "../i18n/ProposalI18nContext";
import type {
  PointOfInterest,
  RankedSpace,
  SpaceAdjustment,
} from "../../ai-mode/types";
import { POI_CATEGORIES } from "../../ai-mode/constants";
import { SpacesLocationMap, POI_COLORS } from "../../map";
import { DEFAULT_MAP_CENTER } from "../../ai-mode/constants";
import { visionService } from "@/services/vision-service";
import { ChatPanel } from "./ChatPanel";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { reportError } from "@/utils/observability";
import {
  curatedRoomPhotos,
  curatedSpacePhotos,
} from "@/features/vision-360/ai-mode/utils/imageCuration";
import {
  buildRoomTrackingContextMap,
  roomTrackingContextFor,
} from "@/features/vision-360/ai-mode/utils/roomTrackingContext";
import { currentDevice } from "@/features/vision-360/ai-mode/utils/proposalSharedContext";
import { mergeDuplicateSpaces } from "@/features/vision-360/proposal-standard/utils";
import { SpaceList } from "./SpaceList";
import { PhotosTab } from "./PhotosTab";
import { AmenitiesTab } from "./AmenitiesTab";
import { MapTab } from "./MapTab";

interface SpacesSectionProps {
  spaces: RankedSpace[];
  companyName?: string;
  targetCity?: string;
  spaceAdjustments?: SpaceAdjustment[];
  selectedSpaceId: string;
  onSelectSpace: (id: string) => void;
  proposalId?: string;
  dealId?: string;
  userEmail?: string;
  clientSegment?: string;
  /** True in partner-demo renders — demo interactions must not pollute the
   * funnel (posicao_no_ranking there is just the partner's manual order). */
  suppressTracking?: boolean;
  showLicenseFee?: boolean;
  hidePrice?: boolean;
}

export function SpacesSection({
  spaces,
  companyName,
  targetCity,
  spaceAdjustments = [],
  selectedSpaceId,
  onSelectSpace,
  proposalId = "",
  dealId,
  userEmail,
  clientSegment,
  suppressTracking = false,
  hidePrice = false,
}: SpacesSectionProps) {
  const { t } = useProposalT();
  const [activeTab, setActiveTab] = useState<
    "fotos" | "mapa" | "amenidades" | "chat"
  >("fotos");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedPOICategories, setSelectedPOICategories] = useState<string[]>(
    [],
  );
  const [pois, setPois] = useState<PointOfInterest[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Whether the Fotos tab shows the private office ("Escritório Privativo") or the
  // building ("Espaço") photos.
  const [photoScope, setPhotoScope] = useState<"office" | "space">("office");
  const prevSpaceId = useRef(selectedSpaceId);

  useEffect(() => {
    if (prevSpaceId.current !== selectedSpaceId) {
      setCurrentImageIndex(0);
      setPhotoScope("office");
      prevSpaceId.current = selectedSpaceId;
    }
  }, [selectedSpaceId]);

  useEffect(() => {
    const partnerIds = spaces
      .map((s) => s.partnerId)
      .filter((id): id is number => id != null);
    if (!partnerIds.length) return;
    visionService
      .getPois(partnerIds)
      .then(setPois)
      .catch((error) => reportError(error, { operation: "getPois" }));
  }, [spaces]);

  const filteredPOIs = useMemo(() => {
    if (!selectedPOICategories.length) return [];
    return pois.filter((poi) => selectedPOICategories.includes(poi.category));
  }, [pois, selectedPOICategories]);

  const selectedSpace = useMemo(
    () => spaces.find((s) => s.id === selectedSpaceId) ?? spaces[0],
    [spaces, selectedSpaceId],
  );

  const selectedSpaceIndex = useMemo(() => {
    const idx = spaces.findIndex((s) => s.id === selectedSpaceId);
    return idx >= 0 ? idx : 0;
  }, [spaces, selectedSpaceId]);

  const selectedDisplayName = useMemo(
    () =>
      selectedSpace
        ? getSpaceDisplayName(
            selectedSpace,
            selectedSpaceIndex,
            t("spaces.option", { number: selectedSpaceIndex + 1 }),
          )
        : "",
    [selectedSpace, selectedSpaceIndex],
  );

  const mapCenter = useMemo(() => {
    if (!selectedSpace) return DEFAULT_MAP_CENTER;
    if (selectedSpace.lat || selectedSpace.lng)
      return { lat: selectedSpace.lat, lng: selectedSpace.lng };
    const withCoords = spaces.filter((sp) => sp.lat || sp.lng);
    if (withCoords.length > 0) {
      return {
        lat: withCoords.reduce((s, sp) => s + sp.lat, 0) / withCoords.length,
        lng: withCoords.reduce((s, sp) => s + sp.lng, 0) / withCoords.length,
      };
    }
    return DEFAULT_MAP_CENTER;
  }, [selectedSpace, spaces]);

  // Room context (partner_id, posicao_no_ranking…) for the match read-out and
  // partner feedback loop. Deduped first so posicao_no_ranking matches the
  // SMB provider's definition (index in the merged suggestedSpaces).
  const roomTrackingContextBySpaceId = useMemo(
    () => buildRoomTrackingContextMap(mergeDuplicateSpaces(spaces)),
    [spaces],
  );

  if (!spaces || spaces.length === 0) return null;

  const selectedAdjustment = spaceAdjustments.find(
    (a) => a.spaceId === selectedSpaceId,
  );
  // Office ("Escritório Privativo") photos and building ("Espaço") photos are shown
  // separately behind a toggle on the Fotos tab. Fall back to the cover image when
  // a room has no curated/room photos (cover-only rooms) so the gallery isn't empty.
  const roomPhotos = curatedRoomPhotos(selectedSpace);
  const officeImages =
    roomPhotos.length > 0
      ? roomPhotos
      : selectedSpace.imageUrl
        ? [selectedSpace.imageUrl]
        : [];
  const spaceImages = curatedSpacePhotos(selectedSpace);
  // The Escritório/Espaço toggle scopes both the photo gallery and the amenities.
  // Show it whenever the building contributes distinct content (photos or amenities).
  const hasSpaceContent =
    spaceImages.length > 0 || (selectedSpace.spaceAmenities?.length ?? 0) > 0;
  const activeScope = hasSpaceContent ? photoScope : "office";
  const galleryImages = activeScope === "space" ? spaceImages : officeImages;

  // Legacy keys + the shared context the SMB provider stamps on every event,
  // so ENT rows survive device/segment cuts in the same dashboards.
  const baseTrackProps = {
    proposal_id: proposalId,
    deal_id: dealId ?? null,
    email: userEmail ?? null,
    proposal_uuid: proposalId,
    device: currentDevice(),
    client_segment: clientSegment || null,
  };

  const handlePhotoScopeChange = (scope: "office" | "space") => {
    setPhotoScope(scope);
    setCurrentImageIndex(0);
  };

  const togglePOICategory = (category: string) => {
    setSelectedPOICategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const handleSelectSpace = (id: string) => {
    const space = spaces.find((s) => s.id === id);
    onSelectSpace(id);
    setCurrentImageIndex(0);
    if (suppressTracking) return;
    void sendTrackDataServer({
      eventName: "VISION_PROPOSAL_SPACE_SELECTED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        ...baseTrackProps,
        ...roomTrackingContextFor(roomTrackingContextBySpaceId, id),
        room_id: id,
        room_name: space?.roomName ?? null,
        room_address: space?.address ?? null,
        room_capacity: space?.capacity ?? null,
        monthly_price: space?.monthlyPrice ?? null,
      },
    });
  };

  const handleTabChange = (tab: "fotos" | "mapa" | "amenidades" | "chat") => {
    setActiveTab(tab);
    if (suppressTracking) return;
    void sendTrackDataServer({
      eventName: "VISION_PROPOSAL_TAB_VIEWED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        ...baseTrackProps,
        ...roomTrackingContextFor(
          roomTrackingContextBySpaceId,
          selectedSpaceId,
        ),
        tab,
        room_id: selectedSpaceId,
        room_name: selectedSpace?.roomName ?? null,
      },
    });
  };

  const monthlyPrice =
    selectedAdjustment?.totalPrice ?? selectedSpace.monthlyPrice ?? 0;
  const clientPrice =
    selectedAdjustment?.clientPrice ?? selectedSpace.monthlyPrice ?? 0;
  const pricePerPos =
    clientPrice > 0 && selectedSpace.capacity > 0
      ? clientPrice / selectedSpace.capacity
      : selectedSpace.pricePerWorkstation;

  return (
    <>
      <section
        id="espacos-selecionados"
        className="py-16 px-6 font-space-grotesk"
        style={{ background: "#F5F3EF", borderTop: "1px solid #E8E6E3" }}
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span
              style={{
                display: "inline-block",
                padding: "6px 16px",
                borderRadius: "999px",
                fontSize: "13px",
                fontWeight: 500,
                color: "#1A1814",
                border: "1px solid #E8E6E3",
                background: "white",
                marginBottom: "12px",
              }}
            >
              {t("spaces.selectedSpaces")}
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold mb-3"
              style={{ color: "#1A1814" }}
            >
              {companyName
                ? t("spaces.optionsFor", { companyName })
                : t("spaces.optionsForDefault")}
            </h2>
            <p
              className="text-base"
              style={{
                color: "#78736D",
                maxWidth: "540px",
                margin: "0 auto",
              }}
            >
              {allSpacesMatchLocation(spaces, targetCity)
                ? t("spaces.bestOfficesInCity", { city: targetCity ?? "" })
                : t("spaces.bestOptions")}
            </p>
          </div>

          {/* Main layout */}
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* LEFT: Space list — desktop only */}
            <div className="hidden md:flex flex-col w-[280px] flex-shrink-0">
              <div
                style={{
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <p
                  style={{
                    color: "#1A1814",
                    fontSize: "16px",
                    fontWeight: 700,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ display: "inline", verticalAlign: "-2px" }}
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M9 21V9" />
                  </svg>
                  {t("spaces.availableOptions")}
                </p>
              </div>

              <div
                className="md:h-[500px] md:overflow-y-auto"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  paddingRight: "4px",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#E8E6E3 transparent",
                }}
              >
                <SpaceList
                  spaces={spaces}
                  spaceAdjustments={spaceAdjustments}
                  selectedSpaceId={selectedSpaceId}
                  onSelectSpace={handleSelectSpace}
                  hidePrice={hidePrice}
                />
              </div>
            </div>

            {/* RIGHT: Tabs + content */}
            <div className="flex-1 min-w-0 w-full">
              {/* Escritório Privativo × Espaço photo scope toggle — full width,
                  above the Fotos/Mapa/Amenidades tabs. Stays visible across the
                  bottom tabs (it drives the Fotos gallery scope). */}
              {hasSpaceContent && (
                <div
                  role="tablist"
                  aria-label={t("spaces.tabNavLabel")}
                  style={{
                    display: "flex",
                    width: "100%",
                    marginBottom: "12px",
                    borderRadius: "12px",
                    border: "1px solid #E8E6E3",
                    padding: "4px",
                    background: "#F5F5F4",
                    boxSizing: "border-box",
                  }}
                >
                  {(
                    [
                      { key: "office", label: t("spaces.scopeOffice") },
                      { key: "space", label: t("spaces.scopeSpace") },
                    ] as const
                  ).map(({ key, label }) => (
                    <button
                      key={key}
                      role="tab"
                      aria-selected={activeScope === key}
                      onClick={() => handlePhotoScopeChange(key)}
                      style={{
                        flex: 1,
                        padding: "8px 16px",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        background:
                          activeScope === key ? "white" : "transparent",
                        color: activeScope === key ? "#1A1814" : "#78736D",
                        boxShadow:
                          activeScope === key
                            ? "0 1px 3px rgba(0,0,0,0.1)"
                            : "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {/* Tab switcher — individual chips (Fotos / Amenidades / Mapa /
                  Dúvidas). Active chip is filled yellow; the rest are white. */}
              <div
                role="tablist"
                aria-label={t("spaces.tabNavLabel")}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                {(
                  [
                    { key: "fotos", label: t("spaces.tabPhotos") },
                    { key: "amenidades", label: t("spaces.tabAmenities") },
                    { key: "mapa", label: t("spaces.tabMap") },
                    { key: "chat", label: t("spaces.tabChat") },
                  ] as const
                ).map(({ key, label }) => (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={activeTab === key}
                    onClick={() => handleTabChange(key)}
                    style={{
                      padding: "7px 18px",
                      borderRadius: "999px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      whiteSpace: "nowrap",
                      background: activeTab === key ? "#FFD900" : "white",
                      color: activeTab === key ? "#1A1814" : "#4B5563",
                      border:
                        activeTab === key
                          ? "1px solid #FFD900"
                          : "1px solid #E8E6E3",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* FOTOS panel */}
              {activeTab === "fotos" && (
                <>
                  <PhotosTab
                    selectedSpace={selectedSpace}
                    displayName={selectedDisplayName}
                    images={galleryImages}
                    currentImageIndex={currentImageIndex}
                    setCurrentImageIndex={setCurrentImageIndex}
                    monthlyPrice={monthlyPrice}
                    pricePerPos={pricePerPos}
                    hidePrice={hidePrice}
                  />
                </>
              )}

              {/* AMENIDADES panel */}
              {activeTab === "amenidades" && (
                <AmenitiesTab
                  selectedSpace={selectedSpace}
                  displayName={selectedDisplayName}
                  scope={activeScope}
                />
              )}

              {/* MAPA panel */}
              {activeTab === "mapa" && (
                <MapTab
                  spaces={spaces}
                  mapCenter={mapCenter}
                  filteredPOIs={filteredPOIs}
                  selectedSpaceId={selectedSpaceId}
                  selectedPOICategories={selectedPOICategories}
                  onTogglePOI={togglePOICategory}
                  onSelectSpace={handleSelectSpace}
                  onFullscreen={() => setIsFullscreen(true)}
                />
              )}
              {/* Chat panel */}
              {activeTab === "chat" && (
                <div role="tabpanel" className="pb-20 md:pb-0">
                  <ChatPanel
                    proposalId={proposalId}
                    dealId={dealId}
                    userEmail={userEmail}
                    clientSegment={clientSegment}
                    suppressTracking={suppressTracking}
                    roomTrackingContext={roomTrackingContextFor(
                      roomTrackingContextBySpaceId,
                      selectedSpaceId,
                    )}
                    spaceName={selectedDisplayName}
                    spaceAddress={
                      selectedSpace?.address
                        ? formatAddressShort(selectedSpace.address)
                        : undefined
                    }
                    spaceCapacity={selectedSpace?.capacity}
                    pricePerPosition={
                      !hidePrice && pricePerPos > 0 ? pricePerPos : undefined
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* Mobile: navigation bar — arrows + dot indicators */}
          {spaces.length > 1 && (
            <div className="md:hidden mt-5">
              <p
                className="text-center text-xs font-semibold mb-2"
                style={{ color: "#78736D" }}
              >
                {t("spaces.option", { number: String(selectedSpaceIndex + 1) })}{" "}
                {t("spaces.ofTotal", { total: String(spaces.length) })}
              </p>
              <div className="flex items-center justify-between px-1">
                <button
                  onClick={() => {
                    const idx = spaces.findIndex(
                      (s) => s.id === selectedSpaceId,
                    );
                    const prev = idx <= 0 ? spaces.length - 1 : idx - 1;
                    handleSelectSpace(spaces[prev].id);
                  }}
                  aria-label="Espaço anterior"
                  className="flex items-center justify-center active:scale-95 transition-transform"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "white",
                    border: "1px solid #E8E6E3",
                    cursor: "pointer",
                    flexShrink: 0,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1A1814"
                    strokeWidth="2.5"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>

                <div className="flex items-center gap-2">
                  {spaces.map((space, index) => (
                    <button
                      key={space.id}
                      onClick={() => handleSelectSpace(space.id)}
                      aria-label={`Espaço ${index + 1}`}
                      style={{
                        width: space.id === selectedSpaceId ? "24px" : "8px",
                        height: "8px",
                        borderRadius: "999px",
                        background:
                          space.id === selectedSpaceId ? "#1A1814" : "#D1CEC9",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        transition: "all 0.3s ease",
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </div>

                <button
                  onClick={() => {
                    const idx = spaces.findIndex(
                      (s) => s.id === selectedSpaceId,
                    );
                    const next = idx >= spaces.length - 1 ? 0 : idx + 1;
                    handleSelectSpace(spaces[next].id);
                  }}
                  aria-label="Próximo espaço"
                  className="flex items-center justify-center active:scale-95 transition-transform"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "white",
                    border: "1px solid #E8E6E3",
                    cursor: "pointer",
                    flexShrink: 0,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1A1814"
                    strokeWidth="2.5"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Fullscreen map overlay */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="absolute top-0 left-0 right-0 bg-white border-b border-[#E8E6E3] px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-[#1A1814]">
              {t("spaces.fullscreenTitle")}
            </h2>
            <button
              onClick={() => setIsFullscreen(false)}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <X className="w-5 h-5 text-[#1A1814]" />
            </button>
          </div>

          <div className="absolute top-24 left-6 right-6 z-10 flex flex-wrap gap-2">
            {POI_CATEGORIES.map((category) => {
              const color = POI_COLORS[category.value];
              const isSelected = selectedPOICategories.includes(category.value);
              return (
                <button
                  key={category.value}
                  onClick={() => togglePOICategory(category.value)}
                  className="shadow-lg transition-all"
                  style={{
                    padding: "6px 12px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                    background: isSelected ? color : "white",
                    color: isSelected ? "white" : "#4b5563",
                    border: `1px solid ${isSelected ? color : "#e5e7eb"}`,
                  }}
                >
                  {t(`poi.${category.value}`)}
                </button>
              );
            })}
          </div>

          <div className="w-full h-full pt-[72px]">
            <SpacesLocationMap
              center={mapCenter}
              zoom={13}
              spaces={spaces}
              pois={filteredPOIs}
              highlightedId={selectedSpaceId}
              onSpaceClick={handleSelectSpace}
              labels={{
                noAddress: t("map.addressNotProvided"),
                recenter: t("map.recenter"),
                recenterTitle: t("map.recenterTitle"),
                poiNoDescription: t("poi.noDescription"),
              }}
              getDisplayName={(space) => {
                const idx = spaces.findIndex((s) => s.id === space.id);
                const i = idx >= 0 ? idx : 0;
                return getSpaceDisplayName(
                  space,
                  i,
                  t("spaces.option", { number: i + 1 }),
                );
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
